import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  getReasonSchema,
  getOptionalReasonSchema,
  type ReasonFormData,
} from '@/schemas/dunningWorkflowActions';

interface Props {
  open: boolean;
  title: string;
  explainer: string;
  confirmLabel: string;
  confirmVariant?: 'default' | 'destructive';
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading?: boolean;
  // optionalReason switches the form to the optional-reason zod schema and
  // tweaks the field label / dialog footer copy. Used by the awaiting_approval
  // cancel modal where supplying a reason is helpful but not required.
  optionalReason?: boolean;
  // dismissLabel overrides the default Cancel button label on the modal's
  // footer. Used when the natural verb for "abort this dialog without
  // confirming" is something other than "Cancel" (e.g. "Keep email").
  dismissLabel?: string;
  // reasonLabel overrides the textarea's field label. Used so callers can
  // supply a context-specific phrasing (e.g. "Reason (optional)" with a
  // caller-controlled message id) without us hard-coding that here.
  reasonLabel?: string;
}

export function ConfirmWithReasonModal({
  open,
  title,
  explainer,
  confirmLabel,
  confirmVariant = 'default',
  onClose,
  onConfirm,
  loading,
  optionalReason,
  dismissLabel,
  reasonLabel,
}: Props) {
  const { i18n } = useLingui();

  const schema = optionalReason ? getOptionalReasonSchema(i18n) : getReasonSchema(i18n);
  const form = useForm<ReasonFormData>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (open) {
      form.reset({ reason: '' });
    }
  }, [open, form]);

  const reasonValue = useWatch({ control: form.control, name: 'reason' }) ?? '';

  const onSubmit = (data: ReasonFormData) => {
    onConfirm(data.reason.trim());
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{explainer}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {reasonLabel ? (
                      reasonLabel
                    ) : optionalReason ? (
                      <Trans id="dunning.action.reason.label-optional">Reason (optional)</Trans>
                    ) : (
                      <Trans>Reason</Trans>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      maxLength={500}
                      aria-required={optionalReason ? 'false' : 'true'}
                      placeholder={i18n._(msg({ id: 'dunning.action.reason.placeholder', message: 'Explain why...' }))}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <div className="text-xs text-muted-foreground text-right">
                    {reasonValue.length}/500
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="mt-4">
              <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
                {dismissLabel ?? <Trans>Cancel</Trans>}
              </Button>
              <Button type="submit" variant={confirmVariant} disabled={loading}>
                {confirmLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
